import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import _ from 'lodash';
import { ReactComponent as CloseIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import i18n from '../../i18n';
import type { FirstMenu, SecondMenu } from '../../models';
import MenuListComponent, { type MenuListItem } from '../dish-menu-creator/menu-list/menu-list-component';
import './scss/menu-library-subcategory-picker-dialog.style.scss';

export type MenuLibrarySubcategoryPickerMode = 'transfer' | 'copy';

export type MenuLibrarySubcategoryPickerDialogProps = {
    open: boolean;
    mode: MenuLibrarySubcategoryPickerMode | null;
    onClose: () => void;
    /** 全店菜单树（与套餐选菜同源，如 `courseGroupPickerMenusAll`） */
    menuData: FirstMenu[];
    loading?: boolean;
    /** 不可选的二级菜单 id（如当前子类别） */
    excludeSluIds: number[];
    confirmLoading?: boolean;
    onConfirm: (secondMenu: SecondMenu) => void | Promise<void>;
};

/**
 * 新建弹窗子分类详情 — 批量转移/复制菜品时选择目标二级菜单（子类别），单选；交互对齐制作器二级菜单弹窗 + MenuListComponent。
 */
export const MenuLibrarySubcategoryPickerDialog: React.FC<MenuLibrarySubcategoryPickerDialogProps> = ({
    open,
    mode,
    onClose,
    menuData,
    loading,
    excludeSluIds,
    confirmLoading,
    onConfirm,
}) => {
    const [selectedSluId, setSelectedSluId] = useState<number | null>(null);
    const [selectedSecondMenu, setSelectedSecondMenu] = useState<SecondMenu | null>(null);

    useEffect(() => {
        if (!open) {
            setSelectedSluId(null);
            setSelectedSecondMenu(null);
        }
    }, [open]);

    const checkedIds = selectedSluId != null ? [selectedSluId] : [];

    const excludeSet = useMemo(() => new Set(excludeSluIds.filter((id) => id != null)), [excludeSluIds]);

    const hasAnySlu = useMemo(() => {
        for (const fm of menuData) {
            for (const slu of fm.menu_item_slu || []) {
                const id = slu.dmi_slu_id;
                if (id != null && !excludeSet.has(id)) {
                    return true;
                }
            }
        }
        return false;
    }, [menuData, excludeSet]);

    const menuTree = useMemo(
        () =>
            menuData.map((item, fmIndex) => ({
                key: `fm-${item.main_group_id ?? 'x'}-${fmIndex}`,
                title: item.main_group_name ?? '',
                children: (item.menu_item_slu || [])
                    .filter((sub) => sub.dmi_slu_id != null && !excludeSet.has(sub.dmi_slu_id as number))
                    .map((subMenuItem) => ({
                        key: `sub-${subMenuItem.dmi_slu_id}`,
                        title: subMenuItem.dmi_slu_name ?? '',
                        dataSource: subMenuItem,
                        checkedSecondMenuIds: checkedIds,
                        originId: subMenuItem.dmi_slu_id,
                    })),
                dataSource: item,
            })),
        [menuData, excludeSet, checkedIds],
    );

    const handleClick = useCallback(
        (menuItem: MenuListItem) => {
            if (menuItem.children) {
                return;
            }
            const menuId = _.get(menuItem, 'dataSource.dmi_slu_id') as number | undefined;
            if (menuId == null) {
                return;
            }
            const sm = menuItem.dataSource as SecondMenu;
            const filterIndex = checkedIds.findIndex((item) => item === menuId);
            if (filterIndex === -1) {
                setSelectedSluId(menuId);
                setSelectedSecondMenu(sm);
            } else {
                setSelectedSluId(null);
                setSelectedSecondMenu(null);
            }
        },
        [checkedIds],
    );

    const title =
        mode === 'transfer'
            ? i18n.t('menu_library_create_dialog_subcategory_picker_title_transfer')
            : mode === 'copy'
                ? i18n.t('menu_library_create_dialog_subcategory_picker_title_copy')
                : '';

    const handleConfirm = () => {
        if (selectedSecondMenu) {
            void onConfirm(selectedSecondMenu);
        }
    };

    return (
        <Dialog
            open={open && mode != null}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            scroll="paper"
            className="menu-library-subcategory-picker-dialog"
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    border: '2px solid var(--fill-primary-300)',
                    backgroundColor: '#ffffff',
                    boxShadow: 'none',
                    overflow: 'hidden',
                },
            }}
            sx={{
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                },
            }}
        >
            <DialogTitle
                className="menu-library-subcategory-picker-dialog__title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    pr: 3,
                    backgroundColor: '#ffffff',
                }}
            >
                <Typography component="span" variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 700 }}>
                    {title}
                </Typography>
                <IconButton
                    type="button"
                    className="header-close-icon"
                    aria-label={i18n.t('extra_cost_text_close')}
                    onClick={onClose}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                className="menu-library-subcategory-picker-dialog__body"
                dividers
                sx={{ backgroundColor: '#ffffff' }}
            >
                {loading && menuData.length === 0 ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={32} />
                    </Box>
                ) : !hasAnySlu ? (
                    <Typography variant="body2" color="text.secondary">
                        {i18n.t('menu_library_create_dialog_subcategory_picker_empty')}
                    </Typography>
                ) : (
                    <Box className="menu-library-subcategory-picker-dialog__list-wrap">
                        <MenuListComponent
                            fullWidth
                            isHideContextMenu
                            menu={menuTree}
                            isShowCheckBox
                            handleClick={handleClick}
                            style={{ maxHeight: 'unset' }}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions
                className="menu-library-subcategory-picker-dialog__actions"
                sx={{ px: 3, pb: 2, backgroundColor: '#ffffff' }}
            >
                <Button color="primary" variant="outlined" onClick={onClose} disabled={!!confirmLoading}>
                    {i18n.t('google_text_public_button_cancel')}
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!selectedSecondMenu || !!confirmLoading || !hasAnySlu}
                    startIcon={
                        confirmLoading ? (
                            <CircularProgress size={18} color="inherit" aria-hidden />
                        ) : undefined
                    }
                >
                    {i18n.t('google_text_confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
